import React from 'react'
import { Dialog, Grid, DialogTitle, DialogContent, makeStyles, Typography, DialogActions } from '@material-ui/core';
import AnnotationTreePanel from "./AnnotationTreePanel";
import { AnnoNode, Annotation } from '../../domain/domain';

const useStyles = makeStyles(theme => ({
  dialogWrapper: {
    padding: theme.spacing(2),
    position: 'absolute',
    height: '100%',
  },
  dialogTitle: {
    paddingLeft: 4,
    paddingRight: 0,
    paddingBottom: 8,
    paddingTop: 0,
  },
  dialogContent: {
    overflow: 'auto',
    padding: 4
  },
  dialogActions: {
    padding: 0
  },
  editorialContentArea: {
    border: '1px solid #ddd',
  },
  approvedDate: {
    fontWeight: 'normal'
  }
}))

type Props = {
  title: string,
  citation: string,
  open: boolean,
  Actions: React.ReactNode,
  children: any,
  showTreeView?: boolean,
  annoNodes?: Annotation,
  onNodeClicked: (node: AnnoNode) => void,
  selectedNodeId?: number
}

const TextEditorDialog: React.FC<Props> = ({ title, onNodeClicked, citation, open, Actions, children, annoNodes, showTreeView = false, selectedNodeId }) => {
  const classes = useStyles();

  const displayApprovedDate = () => {
    if (!showTreeView || !annoNodes || !selectedNodeId) {
      return "";
    }

    // Find the selected node in the annotation tree
    const findNode = (nodes: AnnoNode[]): AnnoNode | undefined => {
      for (const node of nodes) {
        if (node.annoId === selectedNodeId) {
          return node;
        }

        if (node.children && node.children.length > 0) {
          const found = findNode(node.children);
          if (found) return found;
        }
      }
      return undefined;
    };

    // If selectedNodeId is 0, use the root annotation node
    let selectedNode: AnnoNode | Annotation | undefined =
      selectedNodeId === 0 ? annoNodes : findNode(annoNodes.children);

    if (selectedNode?.lastApprovedDate) {
      const approvedDate = new Date(selectedNode.lastApprovedDate);
      return "Last Approved on " + approvedDate.toLocaleDateString();
    } else {
      return "";
    }
  };

  return (
    <Dialog open={open} maxWidth="xl" fullWidth={true} classes={{ paper: classes.dialogWrapper }}>
      <Grid container spacing={showTreeView ? 2 : 0}>
        {showTreeView &&
          <Grid item xs={3}>
            {annoNodes &&
              <AnnotationTreePanel
                treeRoot={annoNodes}
                selectedNodeId={selectedNodeId}
                onNodeClicked={onNodeClicked}
              />
            }
          </Grid>
        }
        <Grid item xs={showTreeView ? 9 : 12} className={showTreeView ? classes.editorialContentArea : ""}>
          <DialogTitle className={classes.dialogTitle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" component="div">
                {title}
              </Typography>
              <Typography variant="h6" component="div" style={{ paddingRight: 8 }}>
                {citation}
              </Typography>
            </div>
            {displayApprovedDate() !== "" && (
              <Typography variant="h5" className={classes.approvedDate}>
                {displayApprovedDate()}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {children}
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            {Actions}
          </DialogActions>
        </Grid>
      </Grid>
    </Dialog>
  )

}

export default TextEditorDialog;
